/* Copyright 2011-2019 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/aspect",
    "dojo/dom-construct",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_Container",
    "dojo/text!./templates/Section.html",
    "./_WidgetMixin",
    "MW/toolstrip/form/SectionDropDownButton",
    "MW/toolstrip/constants/OrientationConstants",
    "mw-form/MenuItem",
    "./SectionPopup",
    "mw-form/mixins/ResizeMixin"
], function (declare, lang, aspect, domConstruct, _WidgetBase, _TemplatedMixin, _Container, template, _WidgetMixin,
             SectionDropDownButton, OrientationConstants, MenuItem, SectionPopup, ResizeMixin) {
             
    function columnSorter(a, b) {
        var result = 0;

        if (a.collapsePriority > b.collapsePriority) {
            result = -1;
        } else if (a.collapsePriority < b.collapsePriority) {
            result = 1;
        }

        return result;
    };
    
    function columnPriorityFilterer(c) {
        return c.collapsePriority >= 0;
    };

    function matchesSelector (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
        var matchesMethod;

        if (!element || !selector) {
            return false;
        }

        matchesMethod = 
            Element.prototype.matches || 
            Element.prototype.matchesSelector || 
            Element.prototype.mozMatchesSelector ||
            Element.prototype.msMatchesSelector || 
            Element.prototype.oMatchesSelector || 
            Element.prototype.webkitMatchesSelector ||
            function(s) {
                var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                    i = matches.length;
                while (--i >= 0 && matches.item(i) !== this) {}
                return i > -1;            
            };

        return matchesMethod.call(element, selector);
    };
    
    var sheddingEnabled = false;
    
    return declare([_WidgetBase, _TemplatedMixin, _Container, _WidgetMixin, ResizeMixin], {
        /** @lends Section# */

        /**
         * @class Section is a Container which can have Controls or Panels as it's children.
         * @extends dijit/_Container
         * @param args
         * <li>title: The text to be displayed in the title of the section.</li>
         */
        templateString: template,
        icon: "section_24",
        _sectionDropDownVisible: false,

        constructor: function () {
            this._isCollapsed = false;
        },

        postCreate: function () {
            this.inherited(arguments);
            if (this.domNode) {
                this.domNode.setAttribute("role", "group");
            }
        },
        
        _setIconAttr: function (value) {
            this.icon = value;
            if (this._sectionDropDownButton) {
                this._sectionDropDownButton.set("icon", this.icon);
            }
        },
        
        _getSortedChildren: function (giveControls) {
            var controlsList = [],
                columns = this.getChildren();
                
            columns = columns.filter(columnPriorityFilterer);
            columns.sort(columnSorter);
            
            if (this._isCompactDisplayMode() || giveControls) {
                columns.forEach(function (column) {
                    column.getChildren().forEach(function (control) {
                        controlsList.push(control);
                    });
                });
                
                return controlsList;
            } else {
                return columns;
            }
        },
        
        _getShedChildren: function () {
            var shedChildren = [];
            
            this._getSortedChildren().forEach(function (control) {if (control._isShed) {shedChildren.push(control);}});
            
            return shedChildren;
        },
        
        _onSectionPopupOpen: function (e) {
            this._sectionPopup.domNode.classList.remove("compactToolstripSectionPopup");
            
            if (this._isCompactDisplayMode()) {
                // Update the SectionPopup classes to reflect the mode
                this._sectionPopup.domNode.classList.add("compactToolstripSectionPopup");
            
                // Remove all children of PopupList/Menu
                var menuChildren = this._sectionPopup.getChildren();
                if (menuChildren.length > 0) {
                    menuChildren.forEach(function (menuItem) {
                        this._sectionPopup.removeChild(menuItem);
                    }.bind(this));
                }
                
                // Update the children in the menu from left to right
                var childControls = this._getSortedChildren(true);
                    
                childControls.forEach(function (control) {
                    if (control._isShed || control.getParent()._isShed) {
                        this._updateControlMenuItemProperties(control);
                
                        /* Add Menu Item to Menu */
                        this._sectionPopup.addChild(control._menuItem);
                    }
                }.bind(this));
            }
            
            this._sectionDropDownButton.showMenu();
        },

        _createSectionPopup: function () {
            this._sectionPopup = new SectionPopup();
            this.own(this._sectionPopup);
            
            this._sectionDropDownButton = new SectionDropDownButton({
                menu: this._sectionPopup,
                label: this.get("title"),
                hasDynamicMenu: true
            });
            if (this.icon) {
                this._sectionDropDownButton.set("icon", this.icon);
            }
            this._sectionDropDownButton.domNode.addEventListener("dropdownclick", this._onSectionPopupOpen.bind(this));
            this._sectionDropDownButton.focusNode.setAttribute("tabIndex", "0");
            this.own(this._sectionDropDownButton);
            this._sectionPopup.domNode.setAttribute("data-focus", "temporary");
            this._sectionDropDownButton.placeAt(this.sectionDropDownContainer);
            this._sectionDropDownButton.startup();
            this._hideSectionDropDownButton();
        },

        _updateControlMenuItemProperties: function (control) {
            var properties = {};

            if (!control._menuItem) {
                this._createControlMenuItem(control);
            }

            if (control.params) {
                properties = control.params;
            } else {
                properties = { // Only accounts for the bare minimum of required properties
                    actionId: control.actionId,
                    text: control.text || "",
                    description: control.description || "",
                    icon: control.icon || "",
                    disabled: control.disabled || false
                };
            }

            Object.keys(properties).forEach(function (key) {
                if (["id", "type"].indexOf(key) < 0) {
                    if (key in control._menuItem) {
                        control._menuItem.set(key, control.get(key));
                    } else if (key === "checked") { // MenuItem does not support 'checked' property, but we use it for ToggleButton
                        control._menuItem.domNode.classList.toggle("mwCheckedTrue", control.get(key));
                    }
                }
            }.bind(this));
        },

        _createControlMenuItem: function (control) {
            var menuItem;
            
            // TODO: Support all Toolstrip control types as Menu-type Items (Need a map and way to import and build dynamically)
            menuItem = new MenuItem();
            menuItem.on("click", function (e) {
                var evt = new Event(e.type);
                Object.setPrototypeOf(evt, e);
                
                control.domNode.dispatchEvent(evt);
            });
            
            control.own(menuItem);
            control._menuItem = menuItem;
        },

        _setTitleAttr: function (value) {
            this.titleNode.textContent = value;
            if (this._sectionDropDownButton) {
                this._sectionDropDownButton.set("label", value);
            }
            this._set("title", value);

            // Set aria-labelledby attribute on the DOM node to the title label so that the screen reader announces
            // the section name when the focus goes to a widget in a new section.
            var titleId = this.id + "_label";
            this.titleNode.setAttribute("id", titleId);
            this.domNode.setAttribute("aria-labelledby", titleId);
        },
        
        _shedComponent: function (component) {
            /* Shed Component */
            // TODO: Check if component supports shedding (e.g. - Galleries do not)
            if (!component._isShed && !component.getParent()._isShed) {
                component.domNode.style.display = "none";
                
                /* Update Component's _isShed property */
                component._isShed = true;
            }
        },
        
        _unshedComponent: function (component) {
            /* Show Component */
            if (component._isShed || component.getParent()._isShed) {
                component.domNode.style.display = "";
                
                /* Update Component's _isShed property */
                component._isShed = false;
            }
        },
        
        _showSectionDropDownButton: function () {
            this._isCompactDisplayMode() ? this.domNode.classList.add("isShed") : this.domNode.classList.add("isCollapsed");
            this._sectionDropDownVisible = true;
        },
        
        _hideSectionDropDownButton: function () {
            this.domNode.classList.remove("isShed");
            this.domNode.classList.remove("isCollapsed");
            this._sectionDropDownVisible = false;
        },
        
        _shouldShedColumns: function () {
            return (sheddingEnabled || this._isCompactDisplayMode());
        },

        collapse: function () {//sectionDropDownContainer
            var parentTab, i, numSkipped, numShed, result, childComponents, lastChildIndex;  
                
            if (this.collapsePriority === -1) {
                this._isCollapsed = true;
                return true;
            }
            
            parentTab = this.getParent();
            i = 0;
            numSkipped = 0;
            numShed = 0;
            result = false; // Not sure what this is used for
            childComponents = this._getSortedChildren();
            
            if (!this._sectionPopup) {
                this._createSectionPopup();
            }
            
            /* TODO: This logic should be more robust to toggling "compact" mode during runtime */
            if (this._shouldShedColumns() && parentTab && parentTab.domNode) {
                /* Set initial metrics */
                containerNodeWidth = parentTab._getWidthOfAllSections();
                tabWidth = parentTab._getWidth();
                    
                if (childComponents.length > 1) {
                    // Loop through children and shed from right to left.
                    i = childComponents.length - 1;
                    lastChildIndex = i;
                    
                    while ((i >= 0) && (tabWidth < containerNodeWidth)) {
                        // Check if already shed
                        if (childComponents[i]._isShed) {
                            i--;
                            numShed++;
                            continue;
                        }
                    
                        /* Shed component */
                        if ((childComponents[i].collapsePriority === -1) || (childComponents[i].getParent().collapsePriority === -1)) {
                            numSkipped++;
                        } else {
                            this._shedComponent(childComponents[i]);
                            numShed++;
                        }
                        
                        if ((numShed > 0) && !this._sectionDropDownVisible) {
                            this._showSectionDropDownButton();
                            var doLoop = true;

                            // Always shed the last two items together
                            while (doLoop && (--i > -1)) {
                                if ((childComponents[i].collapsePriority === -1) || (childComponents[i].getParent().collapsePriority === -1)) {
                                    numSkipped++;
                                } else {
                                    this._shedComponent(childComponents[i]);
                                    numShed++;
                                    doLoop = false;
                                }
                            }
                        }
                        
                        /* Update metrics */
                        containerNodeWidth = parentTab._getWidthOfAllSections();
                        tabWidth = parentTab._getWidth();
                        i -= 1;
                    }
                }
                
                if ((numShed + numSkipped) >= childComponents.length) { // Should never be "greater than", but just in case
                    this._isCollapsed = true;
                } else {
                    this._isCollapsed = false;
                }
            } else {
                if (!(this._isCollapsed || this.collapsePriority === -1)) {
                    result = true;
                    this._isCollapsed = true;
                    this.domNode.classList.add("isCollapsed");
                    domConstruct.place(this.columnContainerWrapper, this._sectionDropDownButton.menu.domNode);
                    this._sectionDropDownButton.focusNode.setAttribute("tabIndex", "0");
                }
            }
            return result;
        },

        expand: function () {
            var parentTab, i, numSkipped, numShed, result, childComponents, doLoop, offset;  
            
            if (this.collapsePriority === -1) {
                return true;
            }
            
            parentTab = this.getParent();
            i = 0;
            result = false; // Not sure what this is used for
            numSkipped = 0;
            numShed = this._getShedChildren().length;
            childComponents = this._getSortedChildren();  
            
            if (!this._sectionPopup) {
                this._createSectionPopup();
            }
            
            /* TODO: This logic should be more robust to toggling "compact" mode during runtime */
            if (this._shouldShedColumns() && parentTab && parentTab.domNode) {
                /* Set initial metrics */
                containerNodeWidth = parentTab._getWidthOfAllSections();
                tabWidth = parentTab._getWidth();
                
                if (childComponents.length > 1) {
                    // Loop through children and unshed from left to right
                    i = childComponents.length - numShed;
                    
                    while ((i < childComponents.length) && (tabWidth > containerNodeWidth)) {
                        /* Unshed controls */
                        if ((childComponents[i].collapsePriority === -1) || (childComponents[i].getParent().collapsePriority === -1)) {
                            numSkipped++;
                        } else {
                            this._unshedComponent(childComponents[i]);
                            numShed--;
                        }
                        
                        if ((numShed < 2) && this._sectionDropDownVisible) {
                            this._hideSectionDropDownButton();
                            doLoop = true;
                            
                            // Always unshed the last two items together
                            while (doLoop && (++i < childComponents.length)) {
                                if ((childComponents[i].collapsePriority === -1) || (childComponents[i].getParent().collapsePriority === -1)) {
                                    numSkipped++;
                                } else {
                                    this._unshedComponent(childComponents[i]);
                                    numShed--;
                                    doLoop = false;
                                }
                            }
                        }
                        
                        /* Update metrics */
                        containerNodeWidth = parentTab._getWidthOfAllSections();
                        tabWidth = parentTab._getWidth();
                        
                        if (tabWidth < containerNodeWidth) {
                            // If we went to far, re-shed the last control
                            this._shedComponent(childComponents[i]);
                            numShed++;
                            
                            // Always shed the last two items together
                            if (numShed < 2) {
                                this._showSectionDropDownButton();
                                offset = 0;
                                doLoop = true;

                                while (doLoop && ((i-(++offset)) > -1)) {
                                    if ((childComponents[(i-offset)].collapsePriority === -1) || (childComponents[(i-offset)].getParent().collapsePriority === -1)) {
                                        numSkipped++;
                                    } else {
                                        this._shedComponent(childComponents[(i-offset)]);
                                        numShed++;
                                        doLoop = false;
                                    }
                                }
                            }
                            
                            result = true;
                            break;
                        }
                        
                        i += 1;
                    }
                }
                
                if ((numShed + numSkipped) >= childComponents.length) { // Should never be "greater than", but just in case
                    this._isCollapsed = true;
                } else {
                    this._isCollapsed = false;
                }
            } else {
                if (this._isCollapsed) {
                    result = true;
                    this._isCollapsed = false;
                    this.domNode.classList.remove("isCollapsed");
                    domConstruct.place(this.columnContainerWrapper, this.sectionWrapper, "first");
                    this._sectionDropDownButton.focusNode.removeAttribute("tabIndex");
                }
            }

            return result;
        },

        toggleDescendantLabels: function (showDescendantLabels) {
            var columns = this.getChildren();
            if (columns) {
                for (var i = 0; i < columns.length; i++) {
					// g1715349: Only hide descendant labels when the column does not have a "width" property set.
			        if (columns[i].get("width") === 0 && columns[i].domNode) {
			            if (showDescendantLabels) {
			                columns[i].domNode.classList.remove("mwHideDescendantLabels");
			                columns[i].set("shouldShedDescendantLabels", false);
			            } else {
                            // g1966275: Only hide the descendant labels when the column is eligible.
                            if (this._isColumnEligibleToHideDescendantLabels(columns[i])) {
                                columns[i].domNode.classList.add("mwHideDescendantLabels");
			                	columns[i].set("shouldShedDescendantLabels", true);
                            }
			            }
			        }
                }
            }
        },

        /**
         * Returns true if all the column children are in horizontal orientation and have labels.
         * @param {Object} column - column widget
         * @returns {boolean}
         * @private
         */
        _isColumnEligibleToHideDescendantLabels: function (column) {
            if (this._isCompactDisplayMode()) {
                return true;
            }
            var children = column.getChildren();
            if (children && children.length > 0) {
                for (var i = 0; i < children.length; i++) {
                    if (children[i].get("orientation") !== OrientationConstants.HORIZONTAL
                        || children[i].get("text") === undefined || children[i].get("text") === "") {
                        return false;
                    }
                }
            }

            return true;
        },

        getPopup: function () {
            return this._sectionPopup;
        },

        closePopup: function () {
            if (this.isPopupOpen()) {
                this._sectionDropDownButton.closeMenu();
            }
        },

        addChild: function (child) {
            this.inherited(arguments);
            if (child.type === "Column") {
                child.updateChildrenOrientations();
            }
        },

        isPopupOpen: function () {
            if(!this._sectionPopup) {
                return false;
            }
            return ((this._sectionDropDownButton.menu.domNode.parentNode) && (this._sectionDropDownButton.menu.domNode.parentNode.style.display !== "none"));
        },
        
        _isCompactDisplayMode: function () {
            // TODO: make this more robust.  We should likely have a local property.
            return this.domNode ? matchesSelector(this.domNode, '.compactToolstrip *') : null;
        }
    });
});
